import React, { useEffect, useRef, useState, useCallback } from "react"
import jsQR from "jsqr"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"

interface QRCameraProps {
  onScan: (data: string) => void
  onError: (error: Error) => void
  aspectRatio?: number
  mirror?: boolean
  showOverlay?: boolean
  overlayMessage?: string
  overlayStyle?: React.CSSProperties
  fullScreen?: boolean
}

const QRCamera: React.FC<QRCameraProps> = ({
  onScan,
  onError,
  aspectRatio = 4 / 3,
  mirror = false,
  showOverlay = true,
  overlayMessage = "",
  overlayStyle = {},
  fullScreen = true,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const animationFrameId = useRef<number | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const lastScanTime = useRef<number>(0)
  const [loading, setLoading] = useState(true)
  const { t } = useTranslation()

  const scanInterval = 300 // ms

  const calculateDimensions = useCallback(() => {
    const vw = window.innerWidth
    const vh = window.innerHeight
    const ar = vw / vh
    if (ar > aspectRatio) {
      return { width: Math.floor(vh * aspectRatio), height: vh }
    } else {
      return { width: vw, height: Math.floor(vw / aspectRatio) }
    }
  }, [aspectRatio])

  const scanQRCode = useCallback(
    (timestamp: number) => {
      const canvas = canvasRef.current
      const video = videoRef.current

      if (!canvas || !video || video.readyState < 2) {
        animationFrameId.current = requestAnimationFrame(scanQRCode)
        return
      }

      const ctx = canvas.getContext("2d", { willReadFrequently: true })
      if (!ctx) {
        animationFrameId.current = requestAnimationFrame(scanQRCode)
        return
      }

      const now = timestamp
      if (now - lastScanTime.current >= scanInterval) {
        lastScanTime.current = now

        const { width, height } = calculateDimensions()
        canvas.width = width
        canvas.height = height

        try {
          ctx.drawImage(video, 0, 0, width, height)
          const imageData = ctx.getImageData(0, 0, width, height)
          const code = jsQR(imageData.data, width, height)

          if (code?.data) {
            console.log("QR Code Detected:", code.data)
            onScan(code.data)
          }
        } catch (error) {
          onError(error as Error)
        }
      }

      animationFrameId.current = requestAnimationFrame(scanQRCode)
    },
    [onScan, onError, calculateDimensions],
  )

  const stopScanner = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop())
      mediaStreamRef.current = null
    }

    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.srcObject = null
    }

    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current)
      animationFrameId.current = null
    }
  }, [])

  async function getPreferredBackCameraStream() {
    // First, ask for permission to access video
    await navigator.mediaDevices.getUserMedia({ video: true })

    const devices = await navigator.mediaDevices.enumerateDevices()
    const videoInputs = devices.filter((device) => device.kind === "videoinput")

    // Filter back-facing cameras
    const backCameras = videoInputs.filter((device) =>
      device.label.toLowerCase().includes("facing back"),
    )

    // Pick the one with lowest index (main back camera)
    const preferredCamera = backCameras.sort((a, b) => {
      const numA = parseInt(a.label.match(/\d+/)?.[0] || "0")
      const numB = parseInt(b.label.match(/\d+/)?.[0] || "0")
      return numA - numB
    })[0]

    let constraints
    if (preferredCamera) {
      constraints = {
        video: {
          deviceId: { exact: preferredCamera.deviceId },
        },
      }
    } else {
      // Fallback
      constraints = {
        video: {
          facingMode: { ideal: "environment" },
        },
      }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      return stream
    } catch (err) {
      console.warn("Preferred camera failed, falling back...", err)
      // Fallback to environment-facing camera
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
        })
        return fallbackStream
      } catch (finalErr) {
        console.error("Both main and fallback camera failed", finalErr)
        throw finalErr
      }
    }
  }

  const startScanner = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      onError(new Error(t("Camera not supported on this device.")))
      setLoading(false)
      return
    }

    setLoading(true)

    try {
      const stream = await getPreferredBackCameraStream()
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        mediaStreamRef.current = stream
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch(onError)
          setLoading(false)
        }
      }
    } catch (initialError) {
      console.warn("Initial camera access failed, trying fallback...", initialError)
    }
  }, [onError, t])

  useEffect(() => {
    startScanner()
    return () => stopScanner()
  }, [startScanner, stopScanner])

  useEffect(() => {
    animationFrameId.current = requestAnimationFrame(scanQRCode)
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current)
        animationFrameId.current = null
      }
    }
  }, [scanQRCode])

  const { width: canvasWidth, height: canvasHeight } = calculateDimensions()

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: fullScreen ? "100vw" : `${canvasWidth}px`,
        height: fullScreen ? "100vh" : `${canvasHeight}px`,
        backgroundColor: "black",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            color: "#fff",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <p>{t("Loading Camera")}...</p>
        </div>
      )}

      <video
        ref={videoRef}
        autoPlay
        playsInline
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: `translate(-50%, -50%) ${mirror ? "scaleX(-1)" : ""}`,
          width: `${canvasWidth}px`,
          height: `${canvasHeight}px`,
          objectFit: "cover",
        }}
      />

      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: `${canvasWidth}px`,
          height: `${canvasHeight}px`,
          transform: "translate(-50%, -50%)",
        }}
      />

      {showOverlay && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: `${canvasWidth * 0.6}px`,
            height: `${canvasHeight * 0.6}px`,
            transform: "translate(-50%, -50%)",
            border: "3px solid white",
            borderRadius: "12px",
            backgroundColor: "rgba(255,255,255,0.1)",
            zIndex: 15,
            pointerEvents: "none",
            ...overlayStyle,
          }}
        >
          {overlayMessage && (
            <div
              style={{
                position: "absolute",
                bottom: "-30px",
                width: "100%",
                textAlign: "center",
                color: "#fff",
                fontSize: "16px",
              }}
            >
              {overlayMessage}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

QRCamera.propTypes = {
  onScan: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  aspectRatio: PropTypes.number,
  mirror: PropTypes.bool,
  showOverlay: PropTypes.bool,
  overlayMessage: PropTypes.string,
  overlayStyle: PropTypes.object,
  fullScreen: PropTypes.bool,
}

export default QRCamera
